import * as React from "react";
import {ReactNode} from "react";
import {FormHelperText, Grid, styled} from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import { IForm } from "../../../../hooks/use_form_v2";
import { ContainerField, Field } from "./Field";
import { Submit } from "./Submit";
import { Buttons, ButtonsContainer } from "./ButtonsContainer";
import { isAfterSubmitButtonElements, isBeforeSubmitButtonElements, isFieldElement } from "./helpers";
import { convertToTranslationKeys, ValidationError } from "../../../../services/validator";
import { t } from "i18next";

const StyledForm = styled('form')(() => ({
    position: 'relative',
}));

const Backdrop = styled('div')(() => ({
    top: 0,
    left: 0,
    position: 'absolute',
    width: '100%',
    height: '100%',
    backgroundColor: 'rgba(255, 255, 255, 0.75)',
}));

const LoadingSpinner = styled(CircularProgress)(() => ({
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginTop: -30,
    marginLeft: -30,
}));

interface IFormProps {
    form: IForm<any>;
    onSubmit(values: any): Promise<any>;
    errors?: ValidationError[];
    withSubmitButton?: boolean;
    submitButtonProps?: any;
    gridProps?: any;
    buttonsContainerGridProps?: any;
    component?: React.ReactElement | any;
    children: ReactNode;
}

export const Form = ({form, onSubmit, errors, withSubmitButton = true, submitButtonProps = {}, gridProps, buttonsContainerGridProps, children, component}: IFormProps) => {
    const handleSubmit = (e: any) => {
        e.preventDefault();

        form.setLoading(true);
        onSubmit(form.getValues())
            .then(() => form.setErrors(undefined))
            .catch(reason => form.setErrors(reason))
            .finally(() => form.setLoading(false));
    };

    const FormComponent = !!component ? component : StyledForm;

    const childrenArray = React.Children.toArray(children);

    return (
        <FormComponent noValidate autoComplete="off" onSubmit={handleSubmit}>
            <Grid
                container
                direction="row"
                justifyContent="space-between"
                alignItems="start"
                spacing={2}
                {...gridProps}
            >
                {childrenArray.filter(isFieldElement)}

                {!!errors && !!errors.length &&
                    <FormHelperText error sx={{margin: 0}}>
                        {convertToTranslationKeys(errors).map(e => t(e)).join(', ')}
                    </FormHelperText>
                }

                <ButtonsContainer gridProps={buttonsContainerGridProps}>
                    {childrenArray.filter(isBeforeSubmitButtonElements)}

                    {!!withSubmitButton &&
                        <Submit loading={form.isLoading()} onSubmit={handleSubmit} {...submitButtonProps} />
                    }

                    {childrenArray.filter(isAfterSubmitButtonElements)}
                </ButtonsContainer>
            </Grid>

            {form.isLoading() && <Backdrop />}
            {form.isLoading() && <LoadingSpinner size={60} />}
        </FormComponent>
    );
}

Form.Field = Field;
Form.ContainerField = ContainerField;
Form.Submit = Submit;
Form.Buttons = Buttons;
